<template>
  <div class="complete-customization">
    {{/* error state */}}
    <callout v-if="error" type="error">
      <div>{{ error }}</div>
    </callout>

    <h2 class="heading">
      Custom Stats

      <material-icon-button
        icon="arrow_back_ios"
        classes="-ico-only -no-container help"
        label="Return to Menu"
        @click-action="closeAction"
      />
    </h2>
    <p>Customize the difficulty and duration of your workout.</p>
    <div class="extras-form">
      <form
        class="standard-form group"
        method="post"
        novalidate="novalidate"
        @submit.prevent="handleSubmit"
      >
        <div class="-flex -between">
          <form-group input-id="difficulty" label="Today's Difficulty" class="-half">
            <div class="select-style">
              <select
                id="difficulty"
                ref="difficulty"
                v-model="formData.difficulty"
                name="difficulty_selector"
              >
                <option
                  v-for="difficulty in difficultyOptions"
                  :key="difficulty"
                  :value="difficulty"
                >
                  {{ difficulty }}
                </option>
              </select>
            </div>
          </form-group>

          <form-group input-id="minutes" label="Duration" class="-half">
            <input
              id="minutes"
              v-model="formData.minutes"
              v-validate="'required|numeric|min_value:1'"
              name="minutes"
              :placeholder="formData.minutes"
              type="number"
              data-vv-as="Minutes"
              min="1"
              class="diff"
            />
          </form-group>
        </div>

        <div class="save-extras -flex -center">
          <submit-button
            :submitting="loading"
            label="Save"
            submitting-label="Processing…"
            class="btn -dark"
            @submit-action="handleSubmit"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'pinia'
import { useCalendarStore } from '@/stores/calendar'
import FormGroup from '@/components/forms/form-group.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import MaterialIconButton from '@/components/buttons/material-icon-button.vue'
import Callout from '@/components/notifications/callout.vue'
import { fbAPIPromise } from '@/utils/http-api'

/**
 * Custom workout complete form for user created custom workouts.
 * Allows user to enter minutes.
 *
 * $emits the following actions
 * - stats-updated
 */
export default {
  name: 'WorkoutTrackerForm',
  components: { SubmitButton, MaterialIconButton, FormGroup, Callout },
  props: {
    scheduledId: {
      type: Number,
      required: true,
    },
    minutes: {
      type: Number,
      required: true,
    },
    difficulty: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      formData: {
        minutes: this.minutes,
        difficulty: this.difficulty,
      },
      difficultyOptions: [1, 2, 3, 4, 5],
      loading: false,
      error: '',
    }
  },

  // lifecycle
  created() {
    this.$validator.updateDictionary({
      en: {
        messages: {
          required: (field) => field + ' required',
          min_value: (field) => field + ' required',
          numeric: (field) => field + ' required',
        },
      },
    })
  },

  methods: {
    ...mapActions(useCalendarStore, ['setTrackers']),

    closeAction() {
      this.$emit('close-action')
    },

    // allow parent component to handle submission
    async handleSubmit() {
      // disable double submit
      if (this.loading) {
        return
      }

      this.loading = true
      this.error = ''

      try {
        // form validation
        const valid = await this.$validator.validateAll()

        if (!valid) {
          this.loading = false
          return
        }
        // end validation

        // we have already toggled isCompleteData, so api url is backwards
        const res = await fbAPIPromise({
          callType: 'post',
          url: `/calendar/schedule/${this.scheduledId}/trackers`,
          skipErrorModal: true,
          body: {
            scheduled_id: this.scheduledId,
            minutes: this.formData.minutes,
            difficulty: this.formData.difficulty,
          },
        })

        // emit program complete stats update to parent
        if (res?.body?.data?.programComplete) {
          const { stats } = res.body.data.programComplete
          this.$emit('stats-updated', stats)
        }

        // update calendar store tracker data
        this.setTrackers(this.scheduledId, {
          minutes: this.formData.minutes,
        })

        this.$nextTick(() => {
          this.closeAction()
        })
      } catch (e) {
        this.error = 'Error saving trackers, please try again'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
